import React, { useState } from 'react';
import Button from '../ui/Button';

export interface BillingDetailsValue {
  billing_name: string;
  state_code: string;
  gstin: string;
  billing_address: string;
}

interface BillingDetailsFormProps {
  /** GST state/UT code -> name, from the engine (GET /billing-profile). */
  states: Record<string, string>;
  initial: BillingDetailsValue;
  saving: boolean;
  /** Server-side reason (engine 422 code), already turned into words. */
  error?: string;
  onSubmit: (value: BillingDetailsValue) => void;
  onCancel: () => void;
}

/**
 * The details a GST tax invoice needs, asked once before the first payment:
 * the name to bill, the state (place of supply decides CGST+SGST vs IGST)
 * and, for a registered practice, its GSTIN. Props-only so it renders in RTL
 * without Firebase/axios; the engine validates for real.
 */
const BillingDetailsForm: React.FC<BillingDetailsFormProps> = ({
  states,
  initial,
  saving,
  error,
  onSubmit,
  onCancel,
}) => {
  const [value, setValue] = useState<BillingDetailsValue>(initial);
  const stateOptions = Object.entries(states).sort((a, b) => a[1].localeCompare(b[1]));
  const hasGstin = value.gstin.trim().length > 0;
  // A GST-registered buyer's invoice must carry their address (Rule 46(d)).
  const ready =
    value.billing_name.trim().length > 0 &&
    value.state_code.length > 0 &&
    (!hasGstin || value.billing_address.trim().length >= 10);

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (ready) {
          onSubmit({
            ...value,
            gstin: value.gstin.trim().toUpperCase(),
            billing_address: value.billing_address.trim(),
          });
        }
      }}
    >
      <p className="text-sm text-parchment-muted-warm">
        We need these for your GST tax invoice. You only enter them once.
      </p>
      <label className="block text-sm">
        <span className="text-chamber-panel">Name on the invoice</span>
        <input
          className="mt-1 w-full border border-parchment-border-strong bg-parchment-card px-3 py-2 text-sm"
          value={value.billing_name}
          maxLength={255}
          onChange={(e) => setValue({ ...value, billing_name: e.target.value })}
          placeholder="Your name or your chamber's name"
          required
        />
      </label>
      <label className="block text-sm">
        <span className="text-chamber-panel">State</span>
        <select
          className="mt-1 w-full border border-parchment-border-strong bg-parchment-card px-3 py-2 text-sm"
          value={value.state_code}
          onChange={(e) => setValue({ ...value, state_code: e.target.value })}
          required
        >
          <option value="">Choose your state</option>
          {stateOptions.map(([code, name]) => (
            <option key={code} value={code}>
              {name}
            </option>
          ))}
        </select>
      </label>
      <label className="block text-sm">
        <span className="text-chamber-panel">GSTIN (optional)</span>
        <input
          className="mt-1 w-full border border-parchment-border-strong bg-parchment-card px-3 py-2 text-sm uppercase"
          value={value.gstin}
          maxLength={15}
          onChange={(e) => setValue({ ...value, gstin: e.target.value })}
          placeholder="Only if your practice is GST-registered"
        />
      </label>
      {hasGstin && (
        <label className="block text-sm">
          <span className="text-chamber-panel">Registered address (as on your GST registration)</span>
          <textarea
            className="mt-1 w-full border border-parchment-border-strong bg-parchment-card px-3 py-2 text-sm"
            rows={2}
            maxLength={500}
            value={value.billing_address}
            onChange={(e) => setValue({ ...value, billing_address: e.target.value })}
            placeholder="Printed on your tax invoice"
          />
        </label>
      )}
      {error && <p className="text-sm text-crimson-600">{error}</p>}
      <div className="flex justify-end gap-2 pt-2 border-t border-parchment-border">
        <Button variant="ghost" size="sm" type="button" onClick={onCancel}>
          Back
        </Button>
        <Button variant="brass" size="sm" type="submit" disabled={!ready || saving}>
          {saving ? 'Saving…' : 'Save and continue'}
        </Button>
      </div>
    </form>
  );
};

export default BillingDetailsForm;
